import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const CHAPTERS = [
  {
    n: "01",
    t: "Preventive over reactive.",
    b: "Most chronic conditions build silently for a decade. Your Twin surfaces the earliest signals so you can act — not react.",
  },
  {
    n: "02",
    t: "Explainable, always.",
    b: "Every score points at the exact answers that shaped it. No black boxes. If the AI thinks you're at risk, it can prove why.",
  },
  {
    n: "03",
    t: "India-first nutrition.",
    b: "Idli, ragi, jowar, sambar, chaas — meal plans built for real Indian kitchens, tailored to your conditions and allergies.",
  },
  {
    n: "04",
    t: "Free. Forever.",
    b: "No premium tier, no paywall. Preventive health should not be a luxury — it should be a habit.",
  },
];

export default function Manifesto() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  // subtle parallax on the huge background number
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);

  return (
    <section
      ref={ref}
      id="manifesto"
      data-testid="section-manifesto"
      className="relative overflow-hidden py-28 sm:py-36"
    >
      {/* Editorial marquee */}
      <div className="border-y border-white/5 py-6 overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee gap-14 text-[38px] sm:text-[56px] font-display font-semibold tracking-tighter">
          <span className="text-white/10">Preventive first.</span>
          <span className="text-cyan-300/40 italic">India-native.</span>
          <span className="text-white/10">Explainable AI.</span>
          <span className="text-cyan-300/40 italic">Free, forever.</span>
          <span className="text-white/10">Preventive first.</span>
          <span className="text-cyan-300/40 italic">India-native.</span>
          <span className="text-white/10">Explainable AI.</span>
          <span className="text-cyan-300/40 italic">Free, forever.</span>
        </div>
      </div>

      {/* Massive parallax numeral */}
      <motion.div
        aria-hidden
        style={{ y: bgY }}
        className="pointer-events-none absolute -right-16 sm:-right-24 top-24 select-none"
      >
        <div className="font-display text-[280px] sm:text-[440px] leading-none font-semibold tracking-tighter text-white/[0.03]">
          04
        </div>
      </motion.div>

      <div className="relative max-w-6xl mx-auto px-6 pt-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl"
        >
          <div className="text-[10px] font-semibold uppercase tracking-[0.4em] text-cyan-300/80">
            The Manifesto
          </div>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.02]">
            Four beliefs we<br />build with.
          </h2>
        </motion.div>

        <div className="mt-14 flex flex-col">
          {CHAPTERS.map((c, i) => (
            <motion.div
              key={c.n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{
                duration: 0.7,
                delay: i * 0.05,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="grid grid-cols-[68px_minmax(0,1fr)] sm:grid-cols-[120px_minmax(0,1fr)_minmax(0,1fr)] gap-4 sm:gap-10 py-8 sm:py-10 border-t border-white/5"
            >
              <div className="font-display text-4xl sm:text-5xl font-semibold tabular-nums text-cyan-300/80">
                {c.n}
              </div>
              <h3 className="font-display text-2xl sm:text-4xl font-semibold leading-tight text-white">
                {c.t}
              </h3>
              <p className="col-span-2 sm:col-span-1 text-zinc-400 leading-relaxed max-w-sm">
                {c.b}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
